// Side-by-side numbers for two sets of trades (two accounts) on the Compare page.
// Everything comes straight from the trades; the breakdowns reuse the Insights
// groupings so both pages agree.
import { buildInsights, holdMinutes } from './insights';
import { formatMoney, grossPnl, netPnl, tradingDayKey } from './pnl';
import { tagKind } from './tags';
import { rootSymbol } from './tools/contracts';
import { translateNow as tn } from './langState';

const MIN_ROW = 3; // trades per side before a breakdown row can become a finding
const SOLID = 10; // below this, a finding is marked as an early observation

const tradesText = (n) => tn(n === 1 ? '{n} trade' : '{n} trades', { n });
const pct = (n) => `${Math.round(n)}%`;

// The headline numbers for one account.
export function summarize(trades) {
  const sorted = [...trades].sort((a, b) => new Date(a.date) - new Date(b.date));
  const nets = sorted.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const grossWin = wins.reduce((a, b) => a + b, 0);
  const grossLoss = -losses.reduce((a, b) => a + b, 0);
  const net = nets.reduce((a, b) => a + b, 0);
  const byDay = {};
  sorted.forEach((t, i) => { const k = tradingDayKey(t.date); byDay[k] = (byDay[k] || 0) + nets[i]; });
  const dayNets = Object.values(byDay);
  // Largest drop from a high point of cumulative net P&L, trade by trade.
  let running = 0;
  let peak = 0;
  let maxDrawdown = 0;
  for (const n of nets) {
    running += n;
    peak = Math.max(peak, running);
    maxDrawdown = Math.max(maxDrawdown, peak - running);
  }
  const holds = sorted.map(holdMinutes).filter((m) => m !== null);
  return {
    count: sorted.length,
    net,
    perTrade: sorted.length ? net / sorted.length : null,
    days: dayNets.length,
    perDay: dayNets.length ? net / dayNets.length : null,
    greenDays: dayNets.length ? (dayNets.filter((n) => n > 0).length / dayNets.length) * 100 : null,
    tradesPerDay: dayNets.length ? sorted.length / dayNets.length : null,
    winRate: sorted.length ? (wins.length / sorted.length) * 100 : null,
    avgWin: wins.length ? grossWin / wins.length : null,
    avgLoss: losses.length ? grossLoss / losses.length : null,
    profitFactor: grossLoss > 0 ? grossWin / grossLoss : grossWin > 0 ? Infinity : null,
    largestWin: wins.length ? Math.max(...wins) : null,
    largestLoss: losses.length ? -Math.min(...losses) : null,
    maxDrawdown,
    fees: sorted.reduce((s, t) => s + (t.fees || 0), 0),
    avgHold: holds.length ? holds.reduce((a, b) => a + b, 0) / holds.length : null,
  };
}

// Cumulative net P&L at the end of each trading day either account traded.
export function equityLines(tradesA, tradesB) {
  const daily = (trades) => {
    const m = {};
    for (const t of trades) m[tradingDayKey(t.date)] = (m[tradingDayKey(t.date)] || 0) + netPnl(t);
    return m;
  };
  const a = daily(tradesA);
  const b = daily(tradesB);
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
  let ra = 0;
  let rb = 0;
  return keys.map((key) => {
    ra += a[key] || 0;
    rb += b[key] || 0;
    return { key, a: ra, b: rb, dayA: a[key] || 0, dayB: b[key] || 0 };
  });
}

function rowStats(trades) {
  const nets = trades.map(netPnl);
  const net = nets.reduce((a, b) => a + b, 0);
  return { count: trades.length, net, avg: trades.length ? net / trades.length : 0, winRate: trades.length ? (nets.filter((n) => n > 0).length / trades.length) * 100 : 0 };
}

function instrumentRows(trades) {
  const map = new Map();
  for (const t of trades) {
    const k = rootSymbol(t.symbol);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(t);
  }
  return [...map.entries()].map(([key, list]) => ({ key, label: key, ...rowStats(list) }));
}

// Lines up two lists of rows by key: [{ key, label, a, b }], a/b null when that side has none.
function merge(rowsA, rowsB, order) {
  const map = new Map();
  for (const r of rowsA) map.set(String(r.key), { key: r.key, label: r.label, a: r, b: null });
  for (const r of rowsB) {
    const k = String(r.key);
    if (map.has(k)) map.get(k).b = r;
    else map.set(k, { key: r.key, label: r.label, a: null, b: r });
  }
  const list = [...map.values()].filter((r) => (r.a && r.a.count) || (r.b && r.b.count));
  if (order === 'key') return list.sort((x, y) => Number(x.key) - Number(y.key));
  // Monday first, Sunday last (key 0 is Sunday).
  if (order === 'weekday') return list.sort((x, y) => ((Number(x.key) + 6) % 7) - ((Number(y.key) + 6) % 7));
  if (order === 'volume') return list.sort((x, y) => ((y.a ? y.a.count : 0) + (y.b ? y.b.count : 0)) - ((x.a ? x.a.count : 0) + (x.b ? x.b.count : 0)));
  return list;
}

// Everything the Compare page shows for accounts A and B.
// `names` = { a, b } for the sentences; `library` tells setups from mistakes.
export function compareAccounts(tradesA, tradesB, { names, library }) {
  const opts = { notes: {}, rules: [], library, since: 0 };
  const ia = buildInsights({ ...opts, trades: tradesA });
  const ib = buildInsights({ ...opts, trades: tradesB });
  const byKind = (rows, kind) => rows.filter((r) => r.tagKind === kind).map((r) => ({ ...r, ...rowStats(r.trades) }));
  const sections = [
    { id: 'time', title: tn('Time of day'), sub: tn('By 30-minute window, in your local time.'), rows: merge(ia.timeRows, ib.timeRows, 'key') },
    { id: 'weekday', title: tn('Day of the week'), rows: merge(ia.weekdayRows, ib.weekdayRows, 'weekday') },
    { id: 'instrument', title: tn('Instrument'), rows: merge(instrumentRows(tradesA), instrumentRows(tradesB), 'volume') },
    { id: 'side', title: tn('Long vs short'), rows: merge(ia.sideRows, ib.sideRows) },
    { id: 'setup', title: tn('Setups'), rows: merge(byKind(ia.tagRows, 'setup'), byKind(ib.tagRows, 'setup'), 'volume') },
    { id: 'mistake', title: tn('Mistakes'), rows: merge(byKind(ia.tagRows, 'mistake'), byKind(ib.tagRows, 'mistake'), 'volume') },
    { id: 'nth', title: tn('Trade number of the day'), rows: merge(ia.nthRows, ib.nthRows, 'key') },
    { id: 'hold', title: tn('Hold time'), rows: merge(ia.holdRows, ib.holdRows, 'key') },
  ];
  const a = summarize(tradesA);
  const b = summarize(tradesB);
  return {
    a, b, sections, findings: findDifferences(a, b, sections, names),
    gap: gapBreakdown(tradesA, tradesB),
    whatIf: { a: whatIfs(tradesA, b, library), b: whatIfs(tradesB, a, library) },
  };
}

// The biggest differences between the two accounts, in plain words, strongest first.
function findDifferences(a, b, sections, names) {
  const out = [];
  if (a.count < 3 || b.count < 3) return out;
  const add = (f) => out.push({ ...f, small: f.sample < SOLID });
  const name = (side) => names[side];
  const other = (side) => (side === 'a' ? 'b' : 'a');
  const sample = Math.min(a.count, b.count);

  // Results per trade.
  const better = a.perTrade >= b.perTrade ? 'a' : 'b';
  const s = { a, b };
  if (Math.abs(a.perTrade - b.perTrade) >= 1) {
    add({ area: tn('Results'), impact: Math.abs(a.perTrade - b.perTrade) * sample, sample,
      text: tn('{better} averages {x} per trade; {worse} averages {y}.', { better: name(better), worse: name(other(better)), x: formatMoney(s[better].perTrade), y: formatMoney(s[other(better)].perTrade) }) });
  }

  // Win rate.
  if (Math.abs(a.winRate - b.winRate) >= 10) {
    const hi = a.winRate > b.winRate ? 'a' : 'b';
    add({ area: tn('Win rate'), impact: (Math.abs(a.winRate - b.winRate) / 100) * sample * Math.max(a.avgWin || 0, b.avgWin || 0) * 0.5, sample,
      text: tn('You win {x} of trades on {hi} but {y} on {lo}.', { hi: name(hi), lo: name(other(hi)), x: pct(s[hi].winRate), y: pct(s[other(hi)].winRate) }) });
  }

  // Size of losses.
  if (a.avgLoss && b.avgLoss) {
    const big = a.avgLoss > b.avgLoss ? 'a' : 'b';
    const ratio = s[big].avgLoss / s[other(big)].avgLoss;
    if (ratio >= 1.3) {
      const lossCount = Math.round(s[big].count * (1 - s[big].winRate / 100));
      add({ area: tn('Losses'), impact: (s[big].avgLoss - s[other(big)].avgLoss) * lossCount, sample: lossCount,
        text: tn('Your average loss on {big} is {x}, {ratio}× the {y} on {small}. Check how your stops are set there.',
          { big: name(big), small: name(other(big)), x: formatMoney(-s[big].avgLoss), y: formatMoney(-s[other(big)].avgLoss), ratio: ratio.toFixed(1) }) });
    }
  }

  // How much you trade.
  if (a.tradesPerDay && b.tradesPerDay) {
    const busy = a.tradesPerDay > b.tradesPerDay ? 'a' : 'b';
    const ratio = s[busy].tradesPerDay / s[other(busy)].tradesPerDay;
    if (ratio >= 1.5) {
      add({ area: tn('Activity'), impact: Math.abs(s[busy].perTrade < 0 ? s[busy].net : 0) + 1, sample: Math.min(a.days, b.days) * 2,
        text: tn('You take {x} trades a day on {busy} vs {y} on {calm}.', { busy: name(busy), calm: name(other(busy)), x: s[busy].tradesPerDay.toFixed(1), y: s[other(busy)].tradesPerDay.toFixed(1) }) });
    }
  }

  // Breakdown rows where the two accounts go different ways.
  for (const sec of sections) {
    for (const r of sec.rows) {
      const ra = r.a && r.a.count >= MIN_ROW ? r.a : null;
      const rb = r.b && r.b.count >= MIN_ROW ? r.b : null;
      if (sec.id === 'mistake') {
        // A mistake that costs one account clearly more than the other.
        for (const side of ['a', 'b']) {
          const row = side === 'a' ? ra : rb;
          const otherRow = side === 'a' ? r.b : r.a;
          if (!row || row.net >= 0) continue;
          const otherNet = otherRow ? otherRow.net : 0;
          if (row.net < otherNet * 2 - 1) {
            add({ area: sec.title, impact: otherNet - row.net, sample: row.count,
              text: otherRow && otherRow.count
                ? tn('"{label}" costs {x} over {n} on {side} vs {y} over {m} on {other}.', { label: r.label, x: formatMoney(row.net), n: tradesText(row.count), side: name(side), y: formatMoney(otherRow.net), m: tradesText(otherRow.count), other: name(other(side)) })
                : tn('"{label}" costs {x} over {n} on {side}, and doesn\'t show up on {other}.', { label: r.label, x: formatMoney(row.net), n: tradesText(row.count), side: name(side), other: name(other(side)) }) });
          }
        }
        continue;
      }
      if (!ra || !rb) continue;
      // Same group, opposite results: one makes money where the other loses.
      if ((ra.net > 0 && rb.net < 0) || (ra.net < 0 && rb.net > 0)) {
        const win = ra.net > 0 ? 'a' : 'b';
        const w = win === 'a' ? ra : rb;
        const l = win === 'a' ? rb : ra;
        add({ area: sec.title, impact: Math.min(w.net, -l.net) + -l.net * 0.5, sample: Math.min(ra.count, rb.count),
          text: tn('{label}: {x} over {n} on {win}, {y} over {m} on {lose}.', { label: r.label, win: name(win), lose: name(other(win)), x: formatMoney(w.net), n: tradesText(w.count), y: formatMoney(l.net), m: tradesText(l.count) }) });
      }
    }
  }

  return out.sort((x, y) => y.impact - x.impact).slice(0, 6);
}

// Where the dollar gap between A and B comes from, split into five parts that add up
// exactly to netA − netB (positive = in A's favour). Per account:
//   net = trades × (winRate × avgWin − (1 − winRate) × avgLoss − avgFees)
// with wins and losses measured before fees. Each part changes one factor and holds the
// rest at the two accounts' average (a midpoint split, so the order doesn't matter).
export function gapBreakdown(tradesA, tradesB) {
  const parts = (trades) => {
    const gross = trades.map(grossPnl);
    const wins = gross.filter((g) => g > 0);
    const rest = gross.filter((g) => g <= 0);
    const n = trades.length;
    const w = n ? wins.length / n : 0;
    const W = wins.length ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
    const L = rest.length ? -rest.reduce((a, b) => a + b, 0) / rest.length : 0;
    const f = n ? trades.reduce((sum, t) => sum + (t.fees || 0), 0) / n : 0;
    return { n, w, W, L, f, e: w * W - (1 - w) * L - f };
  };
  const a = parts(tradesA);
  const b = parts(tradesB);
  const mid = (k) => (a[k] + b[k]) / 2;
  const n = mid('n');
  const items = [
    { id: 'count', value: (a.n - b.n) * mid('e') },
    { id: 'winRate', value: (a.w - b.w) * (mid('W') + mid('L')) * n },
    { id: 'winSize', value: mid('w') * (a.W - b.W) * n },
    { id: 'lossSize', value: -(1 - mid('w')) * (a.L - b.L) * n },
    { id: 'fees', value: -(a.f - b.f) * n },
  ];
  const total = a.n * a.e - b.n * b.e;
  return { total, items, a, b };
}

// Replays one account's trades with a change, to show what a habit cost.
// `other` is the other account's summary (for "trade at its pace").
export function whatIfs(trades, other, library) {
  const sorted = [...trades].sort((x, y) => new Date(x.date) - new Date(y.date));
  const net = (list) => list.reduce((s, t) => s + netPnl(t), 0);
  const base = net(sorted);
  const byDay = new Map();
  for (const t of sorted) {
    const k = tradingDayKey(t.date);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k).push(t);
  }
  const out = [];
  const add = (id, label, kept, note) => {
    if (kept.length === sorted.length) return;
    out.push({ id, label, note, kept: kept.length, removed: sorted.length - kept.length, net: net(kept), change: net(kept) - base });
  };

  // Without trades tagged with a mistake.
  const isMistake = (t) => (t.tags || []).some((tag) => tagKind(tag, library) === 'mistake');
  add('mistakes', tn('Without trades tagged as mistakes'), sorted.filter((t) => !isMistake(t)));

  // Stop for the day after two losses in a row.
  const twoLosses = [];
  for (const day of byDay.values()) {
    let streak = 0;
    for (const t of day) {
      if (streak >= 2) break;
      twoLosses.push(t);
      streak = netPnl(t) < 0 ? streak + 1 : 0;
    }
  }
  add('twoLosses', tn('Stop for the day after 2 losses in a row'), twoLosses);

  // Trade at the other account's pace.
  const mine = byDay.size ? sorted.length / byDay.size : 0;
  if (other && other.tradesPerDay && mine > other.tradesPerDay * 1.2) {
    const cap = Math.max(1, Math.round(other.tradesPerDay));
    const capped = [...byDay.values()].flatMap((day) => day.slice(0, cap));
    add('pace', tn(cap === 1 ? 'Stop after {n} trade a day' : 'Stop after {n} trades a day', { n: cap }), capped, tn('The other account\'s pace.'));
  }

  // Every loss held to your typical (median) loss on that instrument, as if each stop had
  // been respected. Per instrument, so a normal NQ loss isn't "outsized" next to MES.
  const lossesBy = new Map();
  for (const t of sorted) {
    if (netPnl(t) >= 0) continue;
    const k = rootSymbol(t.symbol);
    if (!lossesBy.has(k)) lossesBy.set(k, []);
    lossesBy.get(k).push(netPnl(t));
  }
  const typical = new Map();
  for (const [k, list] of lossesBy) {
    if (list.length < 5) continue;
    list.sort((x, y) => x - y);
    typical.set(k, list[Math.floor(list.length / 2)]);
  }
  const outsized = sorted.filter((t) => typical.has(rootSymbol(t.symbol)) && netPnl(t) < typical.get(rootSymbol(t.symbol)) * 1.5);
  if (outsized.length) {
    const capped = base + outsized.reduce((s, t) => s + (typical.get(rootSymbol(t.symbol)) - netPnl(t)), 0);
    const one = typical.size === 1 ? [...typical.values()][0] : null;
    out.push({ id: 'capLoss',
      label: one !== null ? tn('Losses held to your typical loss ({amount})', { amount: formatMoney(one) }) : tn('Losses held to your typical loss on each instrument'),
      note: tn(outsized.length === 1 ? '{n} loss was more than 1.5× the typical one.' : '{n} losses were more than 1.5× the typical one.', { n: outsized.length }),
      kept: sorted.length, removed: 0, net: capped, change: capped - base, adjusted: outsized.length });
  }
  return { base, scenarios: out };
}
